import React from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion } from '@weezevent/nacre';
import RateItem from './RateItem';
import SeatTitle from './SeatTitle';

function CategoryRates({ seats = [], category, categories, zone, rates, inventories, currentInventory, previousZone, onSelectRate, currency, seatsAndRates }) {
    const { t } = useTranslation();

    const ratesToMap = React.useMemo(() => {
        const actualInventory = inventories.find(inv => inv.uuid === currentInventory);
        const inventory_rates = rates.filter(
            rate => rate.active && rate.inventories && rate.inventories.includes(actualInventory.uuid) && rate.resolved_category === category.uuid
        );
        return inventory_rates;
    }, [inventories, currentInventory, rates]);

    const onChangeForAll = React.useCallback(
        (rate, seats) => {
            // Change rate for all seats of this category
            let seatsAndRatesTmp = [...seatsAndRates];
            seats.forEach(seat => {
                let seatAndRate = seatsAndRatesTmp.find(seatAndRate => seatAndRate.seat === seat && seatAndRate.seat.category === category.uuid);
                seatAndRate.rate = rate;
            });
            onSelectRate(seatsAndRatesTmp);
        },
        [onSelectRate, seatsAndRates]
    );

    const onChangeForOne = React.useCallback(
        (rate, seat) => {
            // Change rate for seat
            let seatsAndRatesTmp = [...seatsAndRates];
            let seatAndRate = seatsAndRatesTmp.find(seatAndRate => seatAndRate.seat === seat);
            seatAndRate.rate = rate;
            onSelectRate(seatsAndRatesTmp);
        },
        [onSelectRate, seatsAndRates]
    );

    const renderRatesForAllSeats = React.useMemo(() => {
        return ratesToMap.map(rate => {
            const filter = seatsAndRates.filter(entry => entry.seat.category === category.uuid);
            let checked = filter.every(seatAndRate => seatAndRate.rate && seatAndRate.rate.pk === rate.id && seatAndRate.seat.category === category.uuid);
            return <RateItem key={rate.id} currency={currency} rate={rate} onChange={rate => onChangeForAll(rate, seats)} checked={checked} />;
        });
    }, [seatsAndRates, category, seats, currency, ratesToMap, onChangeForAll]);

    const renderRatesForOneSeat = React.useMemo(() => {
        return seats.map(seat => (
            <Accordion key={seat.id} title={<SeatTitle seat={seat} color={category.color} categories={categories} />}>
                {ratesToMap.map(rate => {
                    let seatAndRate = seatsAndRates.find(seatAndRate => seatAndRate.seat === seat);
                    let checked = seatAndRate && seatAndRate.rate && seatAndRate.rate.pk === rate.id;
                    return <RateItem key={rate.id} currency={currency} rate={rate} onChange={rate => onChangeForOne(rate, seat)} checked={checked} />;
                })}
            </Accordion>
        ));
    }, [category, seats, seatsAndRates, currency, ratesToMap, onChangeForOne]);

    if (!seats.length || !seatsAndRates.length) {
        return null;
    }

    return (
        <React.Fragment>
            <Accordion
                disableToggle
                openByDefault
                title={
                    seats?.length > 1
                        ? t('attendees.addAttendees.withSeats.popup.rates_plural', {
                              count: seats?.length,
                              category: category.name
                          })
                        : t('attendees.addAttendees.withSeats.popup.rates_singular', { category: category.name })
                }
            >
                {renderRatesForAllSeats}
            </Accordion>
            {renderRatesForOneSeat}
        </React.Fragment>
    );
}

export default function ModalSeatsBody({ seats, zone, categories, rates, inventories, currentInventory, previousZone, seatsAndRates, currency, onSelectRate }) {
    const actualZone = React.useMemo(() => {
        return previousZone?.children?.find(zn => zn.name === zone.name);
    }, [zone, previousZone]);

    return actualZone?.categories?.map(category => {
        return (
            <CategoryRates
                key={category.uuid}
                seats={seats.filter(seat => seat.category === category.uuid)}
                category={category}
                categories={categories}
                zone={zone}
                rates={rates}
                inventories={inventories}
                currentInventory={currentInventory}
                previousZone={previousZone}
                currency={currency}
                color={category.color}
                seatsAndRates={seatsAndRates}
                onSelectRate={onSelectRate}
            />
        );
    });
}
